import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import "./About.css";

const layers = [
  ["website", "Website", "Pages and structure"],
  ["commerce", "Commerce", "Storefront and checkout"],
  ["mobile", "Mobile", "Screens in the hand"],
  ["application", "Application", "Tools and workflows"],
  ["seo", "SEO", "Easier to find"]
];

const stages = [
  ["understand", "Understand", "Business, audience, and constraints"],
  ["design", "Design", "Experience, interface, and flow"],
  ["build", "Build", "The working product"],
  ["improve", "Improve", "The next useful change"]
];

const beliefs = [
  ["purpose", "01", "Purpose over decoration", "Design should make an experience clearer, not simply make it look impressive."],
  ["business", "02", "Built around the business", "Technology should support the way a business operates rather than forcing the business into a predefined system."],
  ["clarity", "03", "Clarity matters", "Clear interfaces, clear content, and clear architecture make digital products easier to use and easier to maintain."],
  ["evolve", "04", "Designed to evolve", "Good digital products should have room to grow as the business and its needs change."]
];

const thinkNodes = [
  ["customer", "Customer", "Who the product is for, and what they need to do."],
  ["workflow", "Workflow", "How the work actually happens inside the business."],
  ["content", "Content", "What the experience has to say, show, and organize."],
  ["technology", "Technology", "Chosen to support the work, after the problem is clear."],
  ["goals", "Goals", "What the business needs the product to accomplish."]
];

const capabilities = [
  ["website", "Websites", "Sites shaped around the brand, the content, and how people use them.", "/services/web-development"],
  ["commerce", "Shopify & Commerce", "Storefronts, products, and a clear path to checkout.", "/services/shopify-development"],
  ["mobile", "Mobile Applications", "Products shaped for the screen in someone's hand.", "/services/mobile-development"],
  ["design", "UI/UX Design", "Interfaces organized around clarity, use, and consistency.", "/services/ui-ux-design"],
  ["application", "Custom Web Applications", "Tools, portals, and dashboards built around a specific workflow.", "/services/custom-applications"],
  ["seo", "Website SEO", "Structure and content that make a site easier to find.", "/services/websiteseo"]
];

const process = [
  ["Discover", "Understand the business, audience, requirements, and goals."],
  ["Plan", "Define the structure, scope, priorities, and technical direction."],
  ["Design", "Create the experience, interface, and interaction system."],
  ["Build", "Develop the actual website, application, commerce experience, or digital product."],
  ["Test", "Review usability, responsiveness, functionality, and technical quality."],
  ["Launch", "Prepare the product for its real environment."],
  ["Improve", "Continue refining the experience as needs evolve."]
];

const audiences = [
  ["growing", "Growing businesses", "Companies that need a stronger digital presence or a better way to connect with customers."],
  ["service", "Service businesses", "Organizations that rely on websites, booking, forms, customer communication, and online visibility."],
  ["commerce", "E-commerce businesses", "Businesses that need a stronger product, storefront, checkout, or commerce experience."],
  ["new", "Teams building something new", "Businesses that need custom applications, internal tools, portals, or a new digital product."]
];

const whyNodes = [
  ["strategy", "Strategy", "Understanding the business and the problem."],
  ["design", "Design", "Creating clear, purposeful digital experiences."],
  ["development", "Development", "Turning the design and requirements into working technology."],
  ["optimization", "Optimization", "Improving the experience after launch."]
];

const approaches = [
  ["01", "No generic templates", "We believe the digital experience should reflect the business, not look like every other website."],
  ["02", "Technology with purpose", "We choose technology based on what the product needs rather than adding complexity for its own sake."],
  ["03", "Design and development together", "The strongest experiences happen when design and development are considered together."],
  ["04", "A foundation for growth", "We build with structure and flexibility so the digital product can evolve as the business does."]
];

const faqs = [
  ["What does BizLogic build?", "Websites, commerce experiences, mobile applications, custom web applications, interface design, and search-ready structure. The work is shaped around how a business actually operates."],
  ["Do you work with small businesses?", "Yes. The starting point is the business, its customers, and the problem in front of it. Size is not the requirement."],
  ["Can you build a website from scratch?", "Yes. A website project can start from the structure, the content, and the way people need to use the site."],
  ["Can you build custom applications?", "Yes. Custom web applications cover tools, portals, and dashboards built around a specific workflow when a generic product does not fit."],
  ["Do you work on existing websites?", "Yes. A project can start from a site that already exists, when the goal is to improve how it is structured, designed, or used."],
  ["Can you help with eCommerce?", "Yes. Commerce work includes storefronts, product presentation, and the path to checkout, including Shopify store development."],
  ["Can you handle design and development together?", "Yes. Design and development are treated as one piece of work, so the interface and the system are built with the same purpose."],
  ["How do we start a project?", "Tell us what you want to build, improve, or solve. The first conversation is about the business and the problem, and it turns into a clear direction from there."]
];

function useInView(threshold = 0.22) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setShown(true);
      return undefined;
    }
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setShown(true);
        observer.disconnect();
      }
    }, { threshold, rootMargin: "0px 0px -8% 0px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, shown];
}

function Reveal({ children, className = "", delay = 0 }) {
  const [ref, shown] = useInView();
  return (
    <div ref={ref} className={`about-reveal ${shown ? "is-in" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function Kicker({ index, label }) {
  return (
    <p className="about-kicker">
      {index ? <span>{index}</span> : null}
      <span>{label}</span>
    </p>
  );
}

function LayerPreview({ kind }) {
  if (kind === "commerce") {
    return (
      <div className="about-ui is-store" aria-hidden="true">
        <b>Store</b>
        <small>Product</small>
        <em>Add</em>
      </div>
    );
  }
  if (kind === "mobile") {
    return (
      <div className="about-ui is-phone" aria-hidden="true">
        <b>Home</b>
        <small>Screen</small>
        <em>Open</em>
      </div>
    );
  }
  if (kind === "application") {
    return (
      <div className="about-ui is-app" aria-hidden="true">
        <b>Tasks</b>
        <small>Review</small>
        <small>Assign</small>
      </div>
    );
  }
  if (kind === "seo") {
    return (
      <div className="about-ui is-search" aria-hidden="true">
        <b>Search</b>
        <small>01 Page</small>
      </div>
    );
  }
  if (kind === "design") {
    return (
      <div className="about-ui is-design" aria-hidden="true">
        <b>Aa</b>
        <i />
        <em>Button</em>
      </div>
    );
  }
  return (
    <div className="about-ui is-web" aria-hidden="true">
      <span className="about-ui-chrome"><i /><i /><i /></span>
      <b>Page</b>
      <small>Headline</small>
      <em>View</em>
    </div>
  );
}

function StageMark({ kind }) {
  if (kind === "design") {
    return (
      <div className="about-stage-mark is-design" aria-hidden="true">
        <b>Layout</b>
        <small>Type</small>
        <small>Action</small>
      </div>
    );
  }
  if (kind === "build") {
    return (
      <div className="about-stage-mark is-build" aria-hidden="true">
        <span>UI</span>
        <span>Code</span>
      </div>
    );
  }
  if (kind === "improve") {
    return (
      <div className="about-stage-mark is-loop" aria-hidden="true">
        <span>Live</span>
        <span>Next</span>
      </div>
    );
  }
  return (
    <div className="about-stage-mark is-notes" aria-hidden="true">
      <b>Brief</b>
      <small>Audience</small>
      <small>Goals</small>
    </div>
  );
}

function BeliefMark({ kind }) {
  if (kind === "business") {
    return (
      <div className="about-mark is-flow" aria-hidden="true">
        <div><b>Customer</b><span>New request</span></div>
        <i />
        <div><b>Workflow</b><span>Review</span></div>
        <i />
        <div><b>System</b><span>Assigned</span></div>
      </div>
    );
  }
  if (kind === "clarity") {
    return (
      <div className="about-mark is-type" aria-hidden="true">
        <b>Heading</b>
        <strong>Plan the next step</strong>
        <p>Clear information helps people understand what happens next.</p>
        <em>Continue</em>
        <div><b>Status</b><span>Ready</span></div>
      </div>
    );
  }
  if (kind === "evolve") {
    return (
      <div className="about-mark is-grow" aria-hidden="true">
        <div><b>Now</b><span>Core experience</span></div>
        <div><b>Next</b><span>New workflow</span></div>
        <div><b>Later</b><span>Additional capability</span></div>
      </div>
    );
  }
  return (
    <div className="about-mark is-purpose" aria-hidden="true">
      <b>Page</b>
      <strong>The point of the screen</strong>
      <p>Choose what matters first.</p>
      <em>View details</em>
      <ul>
        <li><b>Content</b><span>Clear information</span></li>
        <li><b>Action</b><span>One obvious next step</span></li>
      </ul>
    </div>
  );
}

function AudienceMark({ kind }) {
  if (kind === "service") {
    return (
      <div className="about-aud is-form" aria-hidden="true">
        <b>Message</b>
        <small>How can we help?</small>
        <em>Send</em>
      </div>
    );
  }
  if (kind === "commerce") {
    return (
      <div className="about-aud is-product" aria-hidden="true">
        <b>Product</b>
        <small>Store item</small>
        <em>View</em>
      </div>
    );
  }
  if (kind === "new") {
    return (
      <div className="about-aud is-new" aria-hidden="true">
        <b>New</b>
        <small>First version</small>
      </div>
    );
  }
  return (
    <div className="about-aud is-presence" aria-hidden="true">
      <b>Home</b>
      <small>Services</small>
    </div>
  );
}

function HeroSystem() {
  const [active, setActive] = useState(layers[0][0]);
  const current = layers.find(([id]) => id === active) || layers[0];
  return (
    <div className="about-system">
      <ol className="about-path">
        {["Ideas", "Design", "Technology", "Business"].map((label) => <li key={label}>{label}</li>)}
      </ol>
      <div className="about-system-body">
        <div className="about-layers" role="group" aria-label="Connected digital layers">
          {layers.map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={`about-layer ${active === id ? "is-on" : ""}`}
              aria-pressed={active === id}
              onMouseEnter={() => setActive(id)}
              onFocus={() => setActive(id)}
            >
              <span>{label}</span>
              <LayerPreview kind={id} />
            </button>
          ))}
        </div>
        <div className="about-core is-live">
          <span>BizLogic</span>
          <strong>Digital system</strong>
          <p>{current[2]}</p>
        </div>
      </div>
    </div>
  );
}

function ApproachBoard() {
  const [active, setActive] = useState(0);
  return (
    <div className="about-board">
      <p className="about-board-label">The BizLogic approach</p>
      <div className="about-stages">
        {stages.map(([id, title, text], index) => (
          <button
            key={id}
            type="button"
            className={`about-stage ${active === index ? "is-on" : ""}`}
            aria-pressed={active === index}
            onMouseEnter={() => setActive(index)}
            onFocus={() => setActive(index)}
          >
            <span className="about-stage-index">{String(index + 1).padStart(2, "0")}</span>
            <span className="about-stage-copy">
              <strong>{title}</strong>
              <small>{text}</small>
            </span>
            <StageMark kind={id} />
          </button>
        ))}
      </div>
    </div>
  );
}

function ThinkBoard() {
  const [active, setActive] = useState(null);
  const current = thinkNodes.find(([id]) => id === active);
  return (
    <div className="about-think">
      <div className="about-think-modules" role="group" aria-label="Inputs to the business need">
        {thinkNodes.map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`about-think-module ${active === id ? "is-on" : ""}`}
            aria-pressed={active === id}
            onMouseEnter={() => setActive(id)}
            onMouseLeave={() => setActive(null)}
            onFocus={() => setActive(id)}
            onBlur={() => setActive(null)}
          >
            <span>{label}</span>
          </button>
        ))}
      </div>
      <div className={`about-think-work ${active ? "is-live" : ""}`}>
        <div className="about-need">
          <span>Business need</span>
          <strong>{current ? current[1] : "The problem"}</strong>
          <p>{current ? current[2] : "Technology choices come after the business, the customer, and the work are understood."}</p>
        </div>
        <div className={`about-think-canvas is-${active || "idle"}`} aria-hidden="true">
          <div className="about-think-zone is-customer"><b>People</b><p>Customer needs</p></div>
          <div className="about-think-zone is-workflow"><b>Flow</b><p>How the work moves</p></div>
          <div className="about-think-zone is-content"><b>Content</b><p>What information is required</p></div>
          <div className="about-think-zone is-technology"><b>System</b><p>What technology supports it</p></div>
          <div className="about-think-zone is-goals"><b>Outcome</b><p>A clearer digital experience</p></div>
        </div>
        <div className="about-out">
          <span>Digital experience</span>
          <p>The product is shaped after the need is clear.</p>
        </div>
      </div>
    </div>
  );
}

const workspaceTitles = [
  "Project discovery",
  "Project plan",
  "Experience system",
  "Development",
  "Quality review",
  "Ready to launch",
  "Continuous improvement"
];

function WsCard({ title, text, children }) {
  return (
    <article className="about-ws-card">
      <b>{title}</b>
      {text ? <small>{text}</small> : null}
      {children}
    </article>
  );
}

function Workspace({ index }) {
  if (index === 0) {
    return (
      <div className="about-process-visual about-ws">
        <div className="about-ws-grid">
          <WsCard title="Business"><p className="about-ws-line">Growth website</p></WsCard>
          <WsCard title="Audience">
            <ul className="about-ws-chips"><li>Customers</li><li>Team</li></ul>
          </WsCard>
          <WsCard title="Goals"><p className="about-ws-line">Improve the digital experience</p></WsCard>
          <WsCard title="Requirements">
            <ul className="about-ws-chips"><li>Content</li><li>Workflow</li><li>Integrations</li></ul>
          </WsCard>
        </div>
      </div>
    );
  }
  if (index === 1) {
    return (
      <div className="about-process-visual about-ws">
        <div className="about-ws-grid">
          <WsCard title="Scope">
            <ul className="about-ws-board">
              <li><em>Now</em><strong>Core website experience</strong></li>
              <li><em>Next</em><strong>Customer workflows</strong></li>
            </ul>
          </WsCard>
          <WsCard title="Structure">
            <ul className="about-ws-tree"><li>Home</li><li>Services</li><li>Detail</li></ul>
          </WsCard>
          <WsCard title="Priorities">
            <ol className="about-ws-order"><li>User experience</li><li>Content</li><li>Performance</li></ol>
          </WsCard>
          <WsCard title="Technical direction">
            <ul className="about-ws-stack"><li>Interface</li><li className="is-on">System</li><li>Data</li></ul>
          </WsCard>
        </div>
      </div>
    );
  }
  if (index === 2) {
    return (
      <div className="about-process-visual about-ws about-ws-design">
        <div className="about-ws-screen" aria-hidden="true">
          <b>Dashboard</b>
          <div className="about-ws-nav"><span>Overview</span><span className="is-on">Services</span><span>Account</span></div>
          <em>Continue</em>
        </div>
        <div className="about-ws-grid">
          <WsCard title="Navigation"><ul className="about-ws-navlist"><li>Overview</li><li className="is-on">Services</li><li>Account</li></ul></WsCard>
          <WsCard title="Components"><div className="about-ws-comp"><b>Aa</b><em>Button</em></div></WsCard>
          <WsCard title="Typography"><p className="about-ws-line">Clear heading</p></WsCard>
          <WsCard title="Interactions"><div className="about-ws-hit"><span>Continue</span><em>Open</em></div></WsCard>
        </div>
      </div>
    );
  }
  if (index === 3) {
    return (
      <div className="about-process-visual about-ws about-ws-app">
        <aside>
          <b>Build</b>
          <span>Overview</span>
          <span className="is-on">Work</span>
          <span>Data</span>
        </aside>
        <div className="about-ws-grid">
          <WsCard title="Frontend"><ul className="about-ws-checks"><li><i />Interface</li></ul></WsCard>
          <WsCard title="Backend"><ul className="about-ws-checks"><li><i />Application logic</li></ul></WsCard>
          <WsCard title="Integrations"><ul className="about-ws-checks"><li><i />Connected systems</li></ul></WsCard>
          <WsCard title="Data"><ul className="about-ws-checks"><li><i />Structured</li></ul></WsCard>
        </div>
      </div>
    );
  }
  if (index === 4) {
    return (
      <div className="about-process-visual about-ws">
        <div className="about-ws-grid">
          <WsCard title="Responsive">
            <ul className="about-ws-checks"><li><i />Desktop</li><li><i />Tablet</li><li><i />Mobile</li></ul>
          </WsCard>
          <WsCard title="Functionality">
            <ul className="about-ws-checks"><li><i />Navigation</li><li><i />Forms</li><li><i />Interactions</li></ul>
          </WsCard>
          <WsCard title="Content">
            <ul className="about-ws-checks"><li><i />Structure</li><li><i />Links</li></ul>
          </WsCard>
        </div>
      </div>
    );
  }
  if (index === 5) {
    return (
      <div className="about-process-visual about-ws">
        <ol className="about-ws-release">
          {[["Environment", "Production"], ["Content", "Reviewed"], ["Testing", "Complete"], ["Deployment", "Ready"]].map(([label, status]) => (
            <li key={label}><b>{label}</b><em><i />{status}</em></li>
          ))}
        </ol>
      </div>
    );
  }
  return (
    <div className="about-process-visual about-ws">
      <ol className="about-ws-timeline">
        <li><b>Feedback</b><span>New request</span></li>
        <li><b>Iteration</b><span>Refine experience</span></li>
        <li><b>Next</b><span>Plan enhancement</span></li>
      </ol>
    </div>
  );
}

function ProcessTimeline() {
  const [active, setActive] = useState(0);
  const [wide, setWide] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const sync = () => setWide(desktop.matches);
    sync();
    desktop.addEventListener("change", sync);
    return () => desktop.removeEventListener("change", sync);
  }, []);
  const [, text] = process[active];
  const scene = (
    <div className="about-panel" aria-live="polite">
      <div className="about-panel-body" key={active}>
        <p className="about-panel-index">{String(active + 1).padStart(2, "0")}</p>
        <h3>{workspaceTitles[active]}</h3>
        <p className="about-panel-copy">{text}</p>
        <Workspace index={active} />
      </div>
    </div>
  );
  return (
    <>
      <div className="about-process" style={{ "--about-fill": `${(active / (process.length - 1)) * 100}%` }}>
        <div className="about-process-rail" />
        <div className="about-process-fill" />
        {process.map(([stepTitle, stepText], index) => (
          <div key={stepTitle} className={`about-step-block ${!wide && active === index ? "is-open" : ""}`}>
            <button
              type="button"
              className={`about-step ${active === index ? "is-active" : ""}`}
              aria-pressed={active === index}
              onClick={() => setActive(index)}
            >
              <span className="about-step-mark">{String(index + 1).padStart(2, "0")}</span>
              <span className="about-step-copy">
                <span>{stepTitle}</span>
                <span>{stepText}</span>
              </span>
            </button>
            {!wide && active === index ? scene : null}
          </div>
        ))}
      </div>
      {wide ? scene : null}
    </>
  );
}

function WhyMap() {
  const [active, setActive] = useState(null);
  return (
    <div className={`about-why-map ${active ? "is-live" : ""}`}>
      <div className="about-why-core">
        <span>BizLogic</span>
        <p>{active ? whyNodes.find(([id]) => id === active)[1] : "One team"}</p>
      </div>
      {whyNodes.map(([id, title, text]) => (
        <button
          key={id}
          type="button"
          className={`about-why-node is-${id} ${active === id ? "is-on" : ""}`}
          aria-pressed={active === id}
          onMouseEnter={() => setActive(id)}
          onMouseLeave={() => setActive(null)}
          onFocus={() => setActive(id)}
          onBlur={() => setActive(null)}
        >
          <strong>{title}</strong>
          <span>{text}</span>
        </button>
      ))}
    </div>
  );
}

function MethodVisual({ index }) {
  if (index === 1) {
    return (
      <div className="about-scene" aria-hidden="true">
        <p>Choose what the product needs</p>
        <div className="about-scene-options">
          <span>Simple</span>
          <span className="is-on">Fit</span>
          <span>Extra</span>
        </div>
      </div>
    );
  }
  if (index === 2) {
    return (
      <div className="about-scene" aria-hidden="true">
        <p>Considered together</p>
        <div className="about-scene-pair">
          <div><b>Design</b><small>Interface</small></div>
          <div><b>Build</b><small>System</small></div>
        </div>
      </div>
    );
  }
  if (index === 3) {
    return (
      <div className="about-scene" aria-hidden="true">
        <p>Room to grow</p>
        <div className="about-scene-base">
          <span>Next</span>
          <strong>Foundation</strong>
        </div>
      </div>
    );
  }
  return (
    <div className="about-scene" aria-hidden="true">
      <p>This business</p>
      <div className="about-scene-page">
        <b>Page</b>
        <small>For this business</small>
        <em>Open</em>
      </div>
    </div>
  );
}

function MethodBoard() {
  const [active, setActive] = useState(0);
  return (
    <div className="about-method">
      <div className="about-method-list">
        {approaches.map(([index, title, text], item) => (
          <button
            key={title}
            type="button"
            className={`about-method-item ${active === item ? "is-on" : ""}`}
            aria-pressed={active === item}
            onMouseEnter={() => setActive(item)}
            onFocus={() => setActive(item)}
            onClick={() => setActive(item)}
          >
            <span>{index}</span>
            <strong>{title}</strong>
            <small>{text}</small>
          </button>
        ))}
      </div>
      <div className="about-method-visual" aria-live="polite">
        <MethodVisual index={active} />
      </div>
    </div>
  );
}

function FaqList() {
  const [open, setOpen] = useState(null);
  return (
    <div className="border-t border-slate-200">
      {faqs.map(([question, answer], index) => {
        const isOpen = open === index;
        return (
          <div key={question} className={`about-acc border-b border-slate-200 ${isOpen ? "is-open" : ""}`}>
            <button type="button" className="w-full flex items-center justify-between gap-6 py-3.5 text-left" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : index)}>
              <span className="font-semibold text-slate-900 font-heading">{question}</span>
              <span className="about-plus" aria-hidden="true"><i /><i /></span>
            </button>
            <div className={`about-acc-panel ${isOpen ? "is-open" : ""}`}>
              <div><p className="pb-5 text-slate-600 leading-relaxed">{answer}</p></div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CtaVisual() {
  return (
    <div className="about-cta-visual" aria-hidden="true">
      <div className="about-cta-core">
        <span>BizLogic</span>
        <strong>Digital system</strong>
      </div>
      <ul>
        {layers.map(([id, label]) => <li key={id}>{label}</li>)}
      </ul>
    </div>
  );
}

export default function About() {
  useEffect(() => {
    const previous = document.title;
    document.title = "About Us | BizLogic Developers";
    return () => { document.title = previous; };
  }, []);

  return (
    <article data-testid="about-page" className="about-page bg-white">
      <header className="about-hero border-b border-slate-200">
        <div className="about-wrap about-hero-grid">
          <div className="about-hero-copy">
            <p className="about-hero-item about-kicker" style={{ animationDelay: "80ms" }}>
              <span>About BizLogic</span>
            </p>
            <h1 className="about-hero-item about-hero-title text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 font-heading" style={{ animationDelay: "160ms" }}>
              Digital Products Built Around Real Business Needs.
            </h1>
            <p className="about-hero-item about-lead text-lg text-slate-600 leading-relaxed" style={{ animationDelay: "240ms" }}>
              BizLogic designs and builds websites, commerce experiences, mobile applications, custom software, and digital systems around how businesses actually work.
            </p>
            <div className="about-hero-item about-hero-actions" style={{ animationDelay: "320ms" }}>
              <Link to="/contact" className="group about-btn about-btn-primary">
                Start a Project
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
              <Link to="/services" className="group about-btn about-btn-secondary">
                Explore Services
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
          <HeroSystem />
        </div>
      </header>

      <section className="about-section about-who border-b border-slate-200">
        <div className="about-wrap about-split">
          <Reveal>
            <Kicker index="01" label="Who we are" />
            <h2 className="about-title text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Technology Should Make the Business Work Better.</h2>
            <p className="about-lead text-slate-600 leading-relaxed">
              BizLogic is a digital development company focused on building websites, commerce experiences, mobile products, custom applications, and search-ready digital systems. We bring design and development together so the technology supports the way a business actually operates.
            </p>
            <p className="about-lead text-slate-600 leading-relaxed">
              Every business has different customers, workflows, constraints, and goals. Our role is to understand those differences and turn them into digital experiences that are clear, useful, maintainable, and built with purpose.
            </p>
          </Reveal>
          <Reveal delay={80}>
            <ApproachBoard />
          </Reveal>
        </div>
      </section>

      <section className="about-section about-beliefs border-b border-slate-200">
        <div className="about-wrap">
          <Reveal>
            <Kicker index="02" label="What we believe" />
            <h2 className="about-title text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Good Digital Work Starts With Good Thinking.</h2>
          </Reveal>
          <div className="about-after about-belief-grid">
            {beliefs.map(([kind, index, title, text], item) => (
              <Reveal key={title} delay={(item % 2) * 70}>
                <article className="about-belief">
                  <BeliefMark kind={kind} />
                  <p className="about-belief-index">{index}</p>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section about-thinking border-b border-slate-200">
        <div className="about-wrap">
          <Reveal>
            <Kicker index="03" label="How we think" />
            <h2 className="about-title text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">We Start With the Problem, Not the Technology.</h2>
          </Reveal>
          <div className="about-after">
            <ThinkBoard />
          </div>
        </div>
      </section>

      <section className="about-section about-capabilities border-b border-slate-200">
        <div className="about-wrap">
          <Reveal>
            <Kicker index="04" label="What we build" />
            <h2 className="about-title text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">From Websites to Custom Digital Systems.</h2>
          </Reveal>
          <div className="about-after about-caps">
            {capabilities.map(([kind, title, text, href]) => (
              <Link key={href} to={href} className="about-cap">
                <LayerPreview kind={kind} />
                <span>
                  <strong>{title}</strong>
                  <small>{text}</small>
                </span>
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section about-build border-b border-slate-200">
        <div className="about-wrap">
          <div className="about-build-intro">
            <Reveal>
              <Kicker index="05" label="How we work" />
              <h2 className="about-title text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">A Clear Process From First Conversation to Launch.</h2>
              <p className="about-lead text-slate-600 leading-relaxed">Select a step. The work moves from the first conversation through launch, and into what happens after.</p>
            </Reveal>
          </div>
          <ProcessTimeline />
        </div>
      </section>

      <section className="about-section about-fit border-b border-slate-200">
        <div className="about-wrap">
          <Reveal>
            <Kicker index="06" label="Who we work with" />
            <h2 className="about-title text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Different Businesses. Different Problems. One Thoughtful Approach.</h2>
          </Reveal>
          <div className="about-after about-audience-grid">
            {audiences.map(([kind, title, text], item) => (
              <Reveal key={title} delay={(item % 2) * 70}>
                <article className="about-audience">
                  <AudienceMark kind={kind} />
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section border-b border-slate-200">
        <div className="about-wrap">
          <Reveal>
            <Kicker index="07" label="Why BizLogic" />
            <h2 className="about-title text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">One Team Across the Digital Experience.</h2>
          </Reveal>
          <div className="about-after">
            <WhyMap />
          </div>
        </div>
      </section>

      <section className="about-section about-method-section border-b border-slate-200">
        <div className="about-wrap">
          <Reveal>
            <Kicker index="08" label="Our approach" />
            <h2 className="about-title text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Built With Intention. Designed to Last.</h2>
          </Reveal>
          <div className="about-after">
            <MethodBoard />
          </div>
        </div>
      </section>

      <section className="about-section border-b border-slate-200">
        <div className="about-wrap">
          <Reveal>
            <Kicker label="Common questions" />
            <h2 className="about-title text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Questions About BizLogic?</h2>
          </Reveal>
          <div className="about-after">
            <FaqList />
          </div>
        </div>
      </section>

      <section className="about-cta">
        <div className="about-cta-grid" />
        <div className="about-cta-glow" />
        <div className="about-wrap about-cta-layout">
          <div>
            <p className="about-kicker about-kicker-light"><span>Let&apos;s build something useful</span></p>
            <h2 className="about-title text-4xl sm:text-5xl font-bold tracking-tight font-heading">Have an Idea, a Problem, or a Website That Needs to Work Better?</h2>
            <p className="about-lead text-slate-300 leading-relaxed">
              Tell us what you&apos;re trying to build, improve, or solve. We&apos;ll help turn the idea into a clear digital direction.
            </p>
            <div className="about-hero-actions">
              <Link to="/contact" data-testid="about-cta-btn" className="group about-btn about-btn-primary">
                Start a Project
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
              <Link to="/services" className="group about-btn about-btn-ghost">
                Explore Services
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
          <CtaVisual />
        </div>
      </section>
    </article>
  );
}
